import { styled } from "goober";
import { Fragment, type ReactNode } from "react";
import { jitUserProvisioningEnabled } from "../../../../shared/jitProvisioning";
import { ssoIssuerUrlError } from "../../../../shared/ssoIssuerUrl";
import { useSessionTenantOrigin } from "../../../hooks/useSessionTenantOrigin";
import { Box } from "../../Box";
import {
  InstructionsStep,
  StepHeading,
} from "../../ConnectorModal/InstructionsStep";
import { MarkdownText } from "../../ConnectorModal/MarkdownItem";
import { Icon } from "../../Icon";
import { Input } from "../../Input";
import { Markdown } from "../../Markdown";
import { Text } from "../../Text";
import { Toggle } from "../../Toggle";
import type { SsoConfig } from "./constants";
import { CopyableField } from "./CopyableField";
import type { IdentityProvider, MetadataSource } from "./identityProviders";
import { AttributeNamesField } from "./MappingFields";
import { ManualSamlFields, MetadataField } from "./MetadataImport";
import { PrimaryProviderField } from "./PrimaryProviderField";
import type { ScimToken } from "./ScimTokenStatus";
import { ScopesField } from "./ScopesField";
import { DomainField, Field } from "./shared";
import { SyncModeFields } from "./SyncModeFields";
import { ssoRegistrationUrls } from "./urls";

const KEEP_CURRENT = "Leave blank to keep current";

const GuideLabel = styled("div")`
  opacity: 0.3;
`;

export function GuideToggle({
  isOn,
  onToggle,
}: {
  isOn: boolean;
  onToggle: () => void;
}) {
  return (
    <>
      <GuideLabel>
        <Text variant="text-small-md">Guide</Text>
      </GuideLabel>
      <Toggle isOn={isOn} onToggle={onToggle} ariaLabel="Guide" />
      <Icon name="slash" size={12} color="var(--border-alpha)" />
    </>
  );
}

function GuideStep({
  title,
  lead,
  guide,
  showGuide,
  children,
}: {
  title: string;
  lead?: ReactNode;
  guide?: string;
  showGuide: boolean;
  children?: ReactNode;
}) {
  if (!showGuide) {
    return (
      <>
        {lead}
        {children}
      </>
    );
  }
  return (
    <InstructionsStep>
      <StepHeading style={{ marginBottom: guide ? -6 : 0 }}>
        <Text variant="text-regular-md">{title}</Text>
      </StepHeading>
      {guide && <GuideText>{guide}</GuideText>}
      {lead}
      {children}
    </InstructionsStep>
  );
}

function GuideText({ children }: { children: string }) {
  return (
    <MarkdownText>
      <Text variant="text-regular">
        <Markdown>{children}</Markdown>
      </Text>
    </MarkdownText>
  );
}

export function SsoProviderSteps({
  idp,
  config,
  onConfigChange,
  metadataSource,
  onMetadataSourceChange,
  showGuide,
  existing,
  lead,
  identityFields,
  scimToken,
}: {
  idp: IdentityProvider;
  config: SsoConfig;
  onConfigChange: (updater: (prev: SsoConfig) => SsoConfig) => void;
  metadataSource: MetadataSource;
  onMetadataSourceChange: (next: MetadataSource) => void;
  showGuide: boolean;
  existing?: boolean;
  lead?: ReactNode;
  identityFields?: ReactNode;
  scimToken?: ScimToken;
}) {
  const tenantOrigin = useSessionTenantOrigin();
  const oidc = config.protocol === "oidc" ? idp.protocols.oidc : undefined;
  const saml = config.protocol === "saml" ? idp.protocols.saml : undefined;
  const guide = (oidc ?? saml)!.guide;
  const urls = ssoRegistrationUrls(tenantOrigin, config.providerId.trim());
  const jit = jitUserProvisioningEnabled({ syncMode: config.syncMode });
  const issuerError =
    oidc && config.issuer.trim()
      ? ssoIssuerUrlError(config.issuer.trim())
      : null;
  const fieldsProps = { config, onConfigChange, existing };
  const Wrapper = showGuide ? Fragment : InstructionsStep;

  return (
    <>
      <Wrapper>
        {showGuide && (
          <GuideStep
            title={`Step 1: Create the app in ${idp.shortName}`}
            guide={guide.create}
            showGuide
          />
        )}

        <GuideStep
          title={`Step 2: Register 3B in ${idp.shortName}`}
          lead={lead}
          guide={guide.register}
          showGuide={showGuide}
        >
          {identityFields}
          {oidc && (
            <CopyableField
              label={oidc.redirectUriLabel}
              value={urls.oidcRedirectUri}
            />
          )}
          {saml && (
            <>
              <CopyableField label={saml.acsUrlLabel} value={urls.acsUrl} />
              <CopyableField label={saml.entityIdLabel} value={urls.entityId} />
              {existing && (
                <CopyableField
                  label="SP metadata URL"
                  hint="Some IdPs can import the settings above from this URL"
                  value={urls.metadataUrl}
                />
              )}
            </>
          )}
        </GuideStep>

        <GuideStep
          title={`Step 3: Connect ${idp.shortName}`}
          guide={guide.connect}
          showGuide={showGuide}
        >
          {oidc && (
            <>
              <Field>
                <Box horizontal={8} vertical={10}>
                  <Text variant="text-small-md">Issuer URL</Text>
                </Box>
                <Input
                  value={config.issuer}
                  onChange={(e) =>
                    onConfigChange((f) => ({ ...f, issuer: e.target.value }))
                  }
                  placeholder={oidc.issuerPlaceholder}
                  required={!existing}
                  style={{ width: "100%" }}
                />
                {issuerError && (
                  <Box horizontal={8} top={6}>
                    <Text variant="text-small" color="var(--text-danger)">
                      {issuerError}
                    </Text>
                  </Box>
                )}
              </Field>
              <Field>
                <Box horizontal={8} vertical={10}>
                  <Text variant="text-small-md">Client ID</Text>
                </Box>
                <Input
                  value={config.clientId}
                  onChange={(e) =>
                    onConfigChange((f) => ({ ...f, clientId: e.target.value }))
                  }
                  placeholder={
                    existing ? KEEP_CURRENT : "OAuth client ID from your IdP"
                  }
                  required={!existing}
                  style={{ width: "100%" }}
                />
              </Field>
              <Field>
                <Box horizontal={8} vertical={10}>
                  <Text variant="text-small-md">Client secret</Text>
                </Box>
                <Input
                  type="password"
                  value={config.clientSecret}
                  onChange={(e) =>
                    onConfigChange((f) => ({
                      ...f,
                      clientSecret: e.target.value,
                    }))
                  }
                  placeholder={
                    existing
                      ? KEEP_CURRENT
                      : "OAuth client secret from your IdP"
                  }
                  required={!existing}
                  style={{ width: "100%" }}
                />
              </Field>
              <ScopesField
                value={config.scopes}
                onChange={(scopes) => onConfigChange((f) => ({ ...f, scopes }))}
                existing={existing}
              />
            </>
          )}
          {saml &&
            (metadataSource === "metadata" ? (
              <MetadataField
                {...fieldsProps}
                label={saml.metadataLabel}
                placeholder={saml.metadataPlaceholder}
                onEnterManually={() => onMetadataSourceChange("manual")}
              />
            ) : (
              <ManualSamlFields
                {...fieldsProps}
                onUseMetadata={() => onMetadataSourceChange("metadata")}
              />
            ))}
        </GuideStep>

        <GuideStep title="Step 4: Provisioning" showGuide={showGuide}>
          <SyncModeFields
            state={{ syncMode: config.syncMode }}
            onChange={(next) =>
              onConfigChange((f) => ({ ...f, syncMode: next.syncMode }))
            }
            modes={idp.syncModes}
            scimToken={scimToken}
            scimUrlHint={`Enter this as the ${idp.scimUrlTerm} in ${idp.shortName}`}
          />
          {showGuide && config.syncMode === "jit_users_and_groups" && (
            <GuideText>{guide.groups}</GuideText>
          )}
          {showGuide && config.syncMode === "scim" && guide.scim && (
            <GuideText>{guide.scim}</GuideText>
          )}
          {saml && jit && (
            <AttributeNamesField
              mapping={config.mapping}
              presets={saml.mapping}
              replacing={config.replaceMapping}
              onChange={(mapping, replaceMapping) =>
                onConfigChange((f) => ({ ...f, mapping, replaceMapping }))
              }
              existing={existing}
            />
          )}
        </GuideStep>

        <GuideStep title="Step 5: Sign-in domain" showGuide={showGuide}>
          <DomainField
            value={config.domain}
            onChange={(domain) => onConfigChange((f) => ({ ...f, domain }))}
            isNew={!existing}
          />
        </GuideStep>
      </Wrapper>
      <PrimaryProviderField
        providerId={config.providerId}
        isOn={config.isPrimary}
        onToggle={(replaceConfirmed) =>
          onConfigChange((f) => ({
            ...f,
            isPrimary: !f.isPrimary,
            primaryReplaceConfirmed: replaceConfirmed,
          }))
        }
      />
    </>
  );
}
